import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import WhatsAppFab from "@/components/WhatsAppFab";
import { api, formatEUR, formatApiError } from "@/lib/api";
import { Receipt, UserCircle, Download, FloppyDisk, Printer, Key, Image as ImageIcon, Trash } from "@phosphor-icons/react";

export default function Billing() {
    const [invoices, setInvoices] = useState([]);
    const [loading, setLoading] = useState(true);
    const [profile, setProfile] = useState({
        razon_social: "", nif: "", direccion: "", codigo_postal: "",
        ciudad: "", provincia: "", pais: "España",
    });
    const [savingProfile, setSavingProfile] = useState(false);
    const [savedMsg, setSavedMsg] = useState("");
    const [error, setError] = useState("");
    const [viewInvoice, setViewInvoice] = useState(null);
    const [logoUrl, setLogoUrl] = useState(null);
    const [logoSaving, setLogoSaving] = useState(false);
    const [logoMsg, setLogoMsg] = useState("");

    async function load() {
        try {
            const [invRes, profRes, logoRes] = await Promise.all([
                api.get("/invoices"),
                api.get("/profile/fiscal"),
                api.get("/profile/logo"),
            ]);
            setInvoices(invRes.data.items || []);
            setProfile({ ...profile, ...(profRes.data.fiscal_profile || {}) });
            setLogoUrl(logoRes.data?.logo_url || null);
        } catch (e) {
            setError(formatApiError(e, "Error al cargar"));
        } finally {
            setLoading(false);
        }
    }
    useEffect(() => { load(); }, []);  // eslint-disable-line

    async function handleLogoFile(file) {
        if (!file) return;
        if (file.size > 500_000) {
            setError("Logo demasiado grande. Máximo 500 KB.");
            return;
        }
        setError(""); setLogoMsg(""); setLogoSaving(true);
        const reader = new FileReader();
        reader.onload = async () => {
            try {
                const dataUrl = reader.result;
                await api.put("/profile/logo", { logo_data_url: dataUrl });
                setLogoUrl(dataUrl);
                setLogoMsg("Logo guardado");
                setTimeout(() => setLogoMsg(""), 2500);
            } catch (e) {
                setError(formatApiError(e, "No se pudo guardar el logo"));
            } finally {
                setLogoSaving(false);
            }
        };
        reader.onerror = () => { setError("No se pudo leer el archivo"); setLogoSaving(false); };
        reader.readAsDataURL(file);
    }

    async function removeLogo() {
        if (!window.confirm("¿Quitar el logo de tus informes y facturas?")) return;
        setLogoSaving(true); setError("");
        try {
            await api.put("/profile/logo", { logo_data_url: null });
            setLogoUrl(null);
            setLogoMsg("Logo eliminado");
            setTimeout(() => setLogoMsg(""), 2500);
        } catch (e) {
            setError(formatApiError(e, "Error al quitar el logo"));
        } finally {
            setLogoSaving(false);
        }
    }

    async function saveProfile() {
        setSavingProfile(true); setSavedMsg(""); setError("");
        try {
            await api.put("/profile/fiscal", profile);
            setSavedMsg("Datos guardados correctamente");
            setTimeout(() => setSavedMsg(""), 2500);
        } catch (e) {
            setError(formatApiError(e, "Error al guardar"));
        } finally {
            setSavingProfile(false);
        }
    }

    return (
        <div className="min-h-screen flex flex-col">
            <Navbar />
            <main className="flex-1 max-w-7xl mx-auto w-full px-6 md:px-12 py-12">
                <div className="mb-10 pb-8 border-b border-[var(--gf-border)]">
                    <div className="micro-label mb-3">— Facturación</div>
                    <h1 className="serif text-4xl md:text-5xl">Facturas y datos fiscales</h1>
                    <p className="text-[var(--gf-text-secondary)] mt-2">
                        Consulta tus facturas, descárgalas e introduce tus datos fiscales para que aparezcan correctamente.
                    </p>
                </div>

                {error && (
                    <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50 mb-6" data-testid="billing-error">
                        {error}
                    </div>
                )}

                <div className="grid lg:grid-cols-[1fr_1.4fr] gap-8">
                    {/* Perfil fiscal */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="space-y-6">
                        <div className="gf-card-ficha">
                            <div className="flex items-center gap-2 mb-6">
                                <UserCircle size={22} weight="light" className="text-[var(--gf-navy)]" />
                                <span className="micro-label">Datos fiscales</span>
                            </div>

                            <div className="space-y-4">
                                <Field label="Razón social / Nombre" value={profile.razon_social} onChange={(v) => setProfile({ ...profile, razon_social: v })} testId="fp-razon" />
                                <Field label="NIF / CIF" value={profile.nif} onChange={(v) => setProfile({ ...profile, nif: v })} testId="fp-nif" />
                                <Field label="Dirección" value={profile.direccion} onChange={(v) => setProfile({ ...profile, direccion: v })} testId="fp-direccion" />
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="CP" value={profile.codigo_postal} onChange={(v) => setProfile({ ...profile, codigo_postal: v })} testId="fp-cp" />
                                    <Field label="Ciudad" value={profile.ciudad} onChange={(v) => setProfile({ ...profile, ciudad: v })} testId="fp-ciudad" />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Provincia" value={profile.provincia} onChange={(v) => setProfile({ ...profile, provincia: v })} testId="fp-provincia" />
                                    <Field label="País" value={profile.pais} onChange={(v) => setProfile({ ...profile, pais: v })} testId="fp-pais" />
                                </div>
                                {savedMsg && <div className="text-sm text-[var(--gf-success)]" data-testid="fp-saved">✓ {savedMsg}</div>}
                                <button onClick={saveProfile} disabled={savingProfile} className="btn-primary w-full justify-center" data-testid="fp-save">
                                    <FloppyDisk size={16} weight="fill" /> {savingProfile ? "Guardando..." : "Guardar datos"}
                                </button>
                            </div>
                        </div>

                        {/* Logo personalizado (white-label para asesorías) */}
                        <div className="gf-card-ficha" data-testid="logo-upload-card">
                            <div className="flex items-center gap-2 mb-4">
                                <ImageIcon size={22} weight="light" className="text-[var(--gf-navy)]" />
                                <span className="micro-label">Logo personalizado</span>
                            </div>
                            <p className="text-xs text-[var(--gf-text-secondary)] mb-4 leading-relaxed">
                                Sube tu logo y aparecerá en las cabeceras de los informes PDF que emitas. Útil si eres
                                asesoría y quieres firmar tus dictámenes con tu marca. PNG / JPG / WebP · máx 500 KB.
                            </p>
                            {logoUrl ? (
                                <div className="flex items-center gap-4 mb-4">
                                    <img src={logoUrl} alt="Tu logo" className="h-16 w-auto max-w-[160px] object-contain border border-[var(--gf-border)] bg-white p-2" data-testid="logo-preview" />
                                    <button onClick={removeLogo} disabled={logoSaving} className="btn-outline text-xs text-[var(--gf-danger)] border-[var(--gf-danger)]/40" data-testid="logo-remove">
                                        <Trash size={14} /> Quitar
                                    </button>
                                </div>
                            ) : (
                                <div className="mb-4 text-xs text-[var(--gf-text-muted)] italic">Aún no has subido un logo — los informes saldrán con la cabecera estándar.</div>
                            )}
                            <label className="btn-primary w-full justify-center cursor-pointer" data-testid="logo-upload-label">
                                <FloppyDisk size={14} weight="bold" /> {logoSaving ? "Subiendo..." : (logoUrl ? "Reemplazar logo" : "Subir logo")}
                                <input type="file" accept="image/png,image/jpeg,image/webp" onChange={(e) => handleLogoFile(e.target.files?.[0])} className="hidden" data-testid="logo-input" />
                            </label>
                            {logoMsg && <div className="text-sm text-[var(--gf-success)] mt-2" data-testid="logo-saved">✓ {logoMsg}</div>}
                        </div>

                        {/* Seguridad - cambiar contraseña */}
                        <SecurityCard />
                    </motion.div>

                    {/* Facturas */}
                    <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
                        <div className="gf-card">
                            <div className="flex items-center justify-between mb-6">
                                <div className="flex items-center gap-2">
                                    <Receipt size={22} weight="light" className="text-[var(--gf-navy)]" />
                                    <span className="micro-label">Historial de facturas</span>
                                </div>
                            </div>
                            {loading ? (
                                <div className="text-center py-10 text-[var(--gf-text-secondary)]">Cargando...</div>
                            ) : invoices.length === 0 ? (
                                <div className="text-center py-14" data-testid="no-invoices">
                                    <Receipt size={40} weight="light" className="mx-auto mb-4 text-[var(--gf-text-muted)]" />
                                    <p className="text-[var(--gf-text-secondary)] mb-4">
                                        Aún no tienes facturas. Se generarán automáticamente cuando realices tu primer pago.
                                    </p>
                                    <Link to="/pagos" className="btn-gold inline-flex">Ver planes →</Link>
                                </div>
                            ) : (
                                <div className="overflow-x-auto">
                                    <table className="data-table" data-testid="invoices-table">
                                        <thead>
                                            <tr>
                                                <th>Nº Factura</th>
                                                <th>Fecha</th>
                                                <th>Concepto</th>
                                                <th className="text-right">Base</th>
                                                <th className="text-right">IVA</th>
                                                <th className="text-right">Total</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {invoices.map((inv) => (
                                                <tr key={inv.id} data-testid={`inv-row-${inv.id}`}>
                                                    <td className="mono text-sm">{inv.number}</td>
                                                    <td className="text-sm text-[var(--gf-text-secondary)]">
                                                        {new Date(inv.date).toLocaleDateString("es-ES")}
                                                    </td>
                                                    <td className="text-sm">{inv.concept}</td>
                                                    <td className="text-right mono text-sm">{formatEUR(inv.base, 2)}</td>
                                                    <td className="text-right mono text-sm">{formatEUR(inv.iva_amount, 2)}</td>
                                                    <td className="text-right mono font-semibold">{formatEUR(inv.total, 2)}</td>
                                                    <td className="text-right">
                                                        <button onClick={() => setViewInvoice(inv)} className="text-[var(--gf-navy)] hover:text-[var(--gf-gold)] inline-flex items-center gap-1 text-sm" data-testid={`view-inv-${inv.id}`}>
                                                            <Download size={14} /> Ver
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            )}
                        </div>
                    </motion.div>
                </div>
            </main>

            {viewInvoice && <InvoiceModal invoice={viewInvoice} onClose={() => setViewInvoice(null)} />}

            <Footer />
            <WhatsAppFab />
        </div>
    );
}

function Field({ label, value, onChange, testId }) {
    return (
        <div>
            <label className="gf-label">{label}</label>
            <input
                type="text"
                className="gf-input"
                value={value || ""}
                onChange={(e) => onChange(e.target.value)}
                data-testid={testId}
            />
        </div>
    );
}

function SecurityCard() {
    const [current, setCurrent] = useState("");
    const [next, setNext] = useState("");
    const [confirm, setConfirm] = useState("");
    const [loading, setLoading] = useState(false);
    const [msg, setMsg] = useState("");
    const [err, setErr] = useState("");

    async function submit(e) {
        e.preventDefault();
        setMsg(""); setErr("");
        if (next.length < 6) return setErr("La nueva contraseña debe tener al menos 6 caracteres");
        if (next !== confirm) return setErr("Las contraseñas no coinciden");
        setLoading(true);
        try {
            await api.post("/auth/change-password", { current_password: current, new_password: next });
            setMsg("Contraseña actualizada correctamente");
            setCurrent(""); setNext(""); setConfirm("");
            setTimeout(() => setMsg(""), 3000);
        } catch (e) {
            setErr(formatApiError(e, "No se pudo cambiar la contraseña"));
        } finally {
            setLoading(false);
        }
    }

    return (
        <div className="gf-card-ficha" data-testid="security-card">
            <div className="flex items-center gap-2 mb-6">
                <Key size={22} weight="light" className="text-[var(--gf-navy)]" />
                <span className="micro-label">Seguridad · Cambiar contraseña</span>
            </div>
            <form onSubmit={submit} className="space-y-4">
                <div>
                    <label className="gf-label">Contraseña actual</label>
                    <input type="password" required className="gf-input" value={current}
                        onChange={(e) => setCurrent(e.target.value)} data-testid="sec-current" autoComplete="current-password" />
                </div>
                <div>
                    <label className="gf-label">Nueva contraseña</label>
                    <input type="password" required minLength={6} className="gf-input" value={next}
                        onChange={(e) => setNext(e.target.value)} data-testid="sec-new" autoComplete="new-password" />
                </div>
                <div>
                    <label className="gf-label">Confirmar nueva</label>
                    <input type="password" required minLength={6} className="gf-input" value={confirm}
                        onChange={(e) => setConfirm(e.target.value)} data-testid="sec-confirm" autoComplete="new-password" />
                </div>
                {msg && <div className="text-sm text-[var(--gf-success)]" data-testid="sec-saved">✓ {msg}</div>}
                {err && <div className="text-sm text-[var(--gf-danger)] border-l-2 border-[var(--gf-danger)] pl-3 py-2 bg-red-50" data-testid="sec-error">{err}</div>}
                <button type="submit" disabled={loading} className="btn-outline w-full justify-center" data-testid="sec-submit">
                    <Key size={16} /> {loading ? "Guardando..." : "Cambiar contraseña"}
                </button>
            </form>
        </div>
    );
}

function InvoiceModal({ invoice: inv, onClose }) {
    const fp = inv.fiscal_profile || {};
    const em = inv.emisor || {};
    function print() { window.print(); }
    return (
        <div className="fixed inset-0 z-[200] bg-black/60 flex items-center justify-center p-6 overflow-y-auto print:bg-white print:p-0 print:static print:block" data-testid="invoice-modal">
            <div className="bg-white max-w-3xl w-full my-8 border border-[var(--gf-border)] print:border-0 print:my-0 print:max-w-full">
                <div className="p-6 border-b border-[var(--gf-border)] flex items-center justify-between print:hidden">
                    <div>
                        <div className="micro-label mb-1">— Factura</div>
                        <h2 className="serif text-2xl mono">{inv.number}</h2>
                    </div>
                    <div className="flex gap-2">
                        <button onClick={print} className="btn-outline" data-testid="print-invoice"><Printer size={14} /> Imprimir / PDF</button>
                        <button onClick={onClose} className="btn-primary" data-testid="close-invoice">Cerrar</button>
                    </div>
                </div>

                {/* Invoice body - printable */}
                <div className="p-10 print:p-16">
                    <div className="grid grid-cols-2 gap-8 mb-10 pb-8 border-b border-[var(--gf-border)]">
                        <div>
                            <img src="/logo.png" alt="Gestión Fiscal 347" className="h-16 w-auto mb-4 object-contain" />
                            <div className="micro-label mb-2">Emisor</div>
                            <div className="serif text-lg font-medium mb-1">{em.razon_social || "Gestión Fiscal 347"}</div>
                            <div className="text-xs text-[var(--gf-text-secondary)] space-y-0.5 leading-relaxed">
                                <div>NIF: {em.nif || "—"}</div>
                                <div>{em.direccion}</div>
                                <div>{em.ciudad}, {em.pais}</div>
                                <div>{em.email}</div>
                            </div>
                        </div>
                        <div className="text-right">
                            <div className="micro-label mb-3">Factura</div>
                            <div className="serif text-3xl mono">{inv.number}</div>
                            <div className="text-sm text-[var(--gf-text-secondary)] mt-2">
                                Fecha: <span className="mono">{new Date(inv.date).toLocaleDateString("es-ES")}</span>
                            </div>
                            <div className="text-xs text-[var(--gf-text-muted)] mono mt-1">Estado: {inv.status.toUpperCase()}</div>
                        </div>
                    </div>

                    <div className="mb-10">
                        <div className="micro-label mb-3">Facturar a</div>
                        {fp && (fp.razon_social || fp.nif) ? (
                            <div className="text-sm space-y-1">
                                <div className="serif text-lg">{fp.razon_social}</div>
                                <div>NIF: {fp.nif}</div>
                                <div>{fp.direccion}</div>
                                <div>{fp.codigo_postal} {fp.ciudad}, {fp.provincia}</div>
                                <div>{fp.pais}</div>
                            </div>
                        ) : (
                            <div className="text-sm text-[var(--gf-text-muted)]">
                                {inv.user_email}
                                <div className="text-xs mt-2 italic">
                                    💡 Rellena tus datos fiscales para que aparezcan en futuras facturas.
                                </div>
                            </div>
                        )}
                    </div>

                    <table className="w-full mb-8">
                        <thead>
                            <tr className="border-b-2 border-[var(--gf-navy)]">
                                <th className="text-left py-3 text-xs uppercase tracking-widest text-[var(--gf-text-secondary)]">Concepto</th>
                                <th className="text-right py-3 text-xs uppercase tracking-widest text-[var(--gf-text-secondary)]">Importe</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-b border-[var(--gf-border)]">
                                <td className="py-4">{inv.concept}</td>
                                <td className="py-4 text-right mono">{formatEUR(inv.base, 2)}</td>
                            </tr>
                        </tbody>
                    </table>

                    <div className="grid grid-cols-2 gap-4">
                        <div></div>
                        <div className="space-y-2 text-sm">
                            <div className="flex justify-between">
                                <span className="text-[var(--gf-text-secondary)]">Base imponible</span>
                                <span className="mono">{formatEUR(inv.base, 2)}</span>
                            </div>
                            <div className="flex justify-between">
                                <span className="text-[var(--gf-text-secondary)]">IVA ({Math.round((inv.iva_rate || 0.21) * 100)}%)</span>
                                <span className="mono">{formatEUR(inv.iva_amount, 2)}</span>
                            </div>
                            <div className="flex justify-between pt-3 border-t-2 border-[var(--gf-navy)]">
                                <span className="serif text-lg">Total</span>
                                <span className="serif text-2xl mono">{formatEUR(inv.total, 2)}</span>
                            </div>
                        </div>
                    </div>

                    <div className="mt-12 pt-6 border-t border-[var(--gf-border)] text-xs text-[var(--gf-text-muted)] text-center">
                        Factura pagada · Gracias por confiar en Gestión Fiscal 347 · info@gf347.es
                    </div>
                </div>
            </div>
        </div>
    );
}
